import React, { useCallback, useEffect, useState } from "react";
import { FaBullhorn, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaSyncAlt } from "react-icons/fa";

// Weekly social promo review (admins only) — 2026-10-11.
//
// Publishing the YouTube LIVE event creates the promo_jobs row (database
// trigger, db/promo-jobs-on-broadcast.sql); scripts/promo_worker.py on
// Bhaskar's Mac picks it up at once and prepares the Instagram poster and the
// bilingual reel. Here an admin watches the reel, edits script lines, and
// either approves (the worker publishes: reel -> Instagram, Facebook, YouTube
// Shorts; poster -> Facebook) or asks for a re-render. Nothing is published
// without the Approve click.

const card = "border border-kk-gold/15 rounded-xl bg-white/[0.02]";
const btn = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 disabled:opacity-40";

const STATUS = {
  requested: ["queued — the Mac worker starts on it right away", "bg-sky-500/15 text-sky-300"],
  preparing: ["preparing — poster, voice and reel (a few minutes)", "bg-amber-500/15 text-amber-300"],
  ready: ["ready for review", "bg-kk-gold/15 text-kk-gold"],
  changes_requested: ["re-rendering with your changes", "bg-amber-500/15 text-amber-300"],
  approved: ["approved — publishing now", "bg-sky-500/15 text-sky-300"],
  publishing: ["publishing", "bg-amber-500/15 text-amber-300"],
  published: ["published", "bg-green-500/15 text-green-300"],
  failed: ["needs attention", "bg-red-500/15 text-red-300"],
  skipped: ["skipped", "bg-white/10 text-white/50"],
};
const BUSY = ["requested", "preparing", "changes_requested", "approved", "publishing"];

const Lines = ({ title, lines, onChange, disabled }) => (
  <div className="space-y-2">
    <div className="text-[11px] uppercase tracking-wide text-white/40">{title}</div>
    {lines.map((l, i) => (
      <div key={i} className="rounded-lg bg-white/[0.03] border border-white/5 p-2 space-y-1">
        <div className="flex items-center gap-2 text-[10px] text-white/30">
          <span className="font-mono">#{i + 1}</span><span>camera: {l.focus || "full"}</span>
        </div>
        <textarea value={l.text} disabled={disabled} rows={2}
          onChange={(e) => onChange(i, { ...l, text: e.target.value })}
          className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-kk-cream" title="What the voice says" />
        <input value={l.caption ?? l.text} disabled={disabled}
          onChange={(e) => onChange(i, { ...l, caption: e.target.value })}
          className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-white/70" title="On-screen caption" />
      </div>
    ))}
  </div>
);

export default function PromoPanel({ db, episodeId, me }) {
  const [row, setRow] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [te, setTe] = useState([]);
  const [en, setEn] = useState([]);
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await db.from("promo_jobs").select("*").eq("episode_id", episodeId).maybeSingle();
    if (error) { setErr(error.message); setLoaded(true); return; }
    setErr("");
    setRow(data);
    setLoaded(true);
    if (data && !dirty) {
      setTe(data.scripts?.te || []);
      setEn(data.scripts?.en || []);
    }
  }, [db, episodeId, dirty]);

  useEffect(() => { load(); }, [load]);
  // While the worker is busy, check back every 20 s.
  useEffect(() => {
    if (!row || !BUSY.includes(row.status)) return undefined;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [row, load]);

  const update = async (fields, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setSaving(true);
    const by = me?.name || me?.username || "admin";
    const q = row
      ? db.from("promo_jobs").update({ ...fields, reviewed_by: by, reviewed_at: new Date().toISOString() }).eq("episode_id", episodeId)
      : db.from("promo_jobs").insert({ episode_id: episodeId, ...fields, reviewed_by: by });
    const { error } = await q;
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setDirty(false);
    setNotes("");
    load();
  };

  const edit = (setter) => (i, line) => { setter((ls) => ls.map((l, k) => (k === i ? line : l))); setDirty(true); };

  if (!loaded) return null;
  const [label, cls] = STATUS[row?.status] || ["not prepared yet", "bg-white/10 text-white/50"];
  const busy = row && BUSY.includes(row.status);
  const expired = row?.assets_expire_at && new Date(row.assets_expire_at) < new Date();
  const links = row?.publish_result
    ? Object.values(row.publish_result).flatMap((r) => (r.platforms || []).map((p) => p))
    : [];

  return (
    <section className={`${card} p-4 space-y-3`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-kk-gold flex items-center gap-2">
          <FaBullhorn /> Social promo
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>
          {busy && <FaSpinner className="animate-spin text-white/40" />}
        </h2>
        <button onClick={load} title="Refresh" className={`${btn} bg-white/5 text-white/60 hover:text-white`}><FaSyncAlt /></button>
      </div>

      {err && <p className="text-xs text-red-300/80">{err.includes("promo_jobs") ? "The promo_jobs table isn't set up yet (db/promo-jobs.sql)." : err}</p>}

      {!row && !err && (
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-[11px] text-white/40">
            Prepared automatically as soon as the YouTube LIVE event is published.
          </p>
          <button disabled={saving} onClick={() => update({ status: "requested" })}
            className={`${btn} bg-kk-gold text-kk-ink hover:bg-kk-amber`}>Prepare now</button>
        </div>
      )}

      {row?.status === "failed" && (
        <div className="text-xs text-red-300/80 flex items-start gap-2">
          <FaExclamationTriangle className="mt-0.5 shrink-0" />
          <span className="whitespace-pre-wrap">{row.error_message}</span>
        </div>
      )}

      {row?.reel_url && (
        <div className="grid md:grid-cols-[minmax(0,260px)_minmax(0,220px)_1fr] gap-4 items-start">
          <div className="space-y-1">
            <video key={row.reel_url} src={row.reel_url} controls playsInline className="w-full rounded-lg border border-white/10 bg-black" />
            <div className="text-[10px] text-white/30">reel · {row.reel_seconds}s{expired ? " · preview link expired — refresh after a re-render" : ""}</div>
          </div>
          <div className="space-y-1">
            <img src={row.poster_url} alt="Instagram poster" className="w-full rounded-lg border border-white/10" />
            <div className="text-[10px] text-white/30">poster (Facebook) · reel goes to Instagram, Facebook, YouTube Shorts</div>
          </div>
          <div className="space-y-3 max-h-[640px] overflow-auto pr-1">
            <Lines title="Telugu" lines={te} onChange={edit(setTe)} disabled={busy || row.status === "published"} />
            <Lines title="English (Telugu names in Telugu script)" lines={en} onChange={edit(setEn)} disabled={busy || row.status === "published"} />
          </div>
        </div>
      )}

      {row && !busy && row.status !== "published" && (
        <div className="space-y-2">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="Change request for a fresh draft (optional), e.g. 'mention that this is our 375th episode'"
            className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-kk-cream placeholder:text-white/20" />
          <div className="flex items-center gap-2 flex-wrap">
            {row.status === "ready" && !dirty && !notes && (
              <button disabled={saving} className={`${btn} bg-green-600 text-white hover:bg-green-500`}
                onClick={() => update({ status: "approved" },
                  "Publish now? The reel goes to Instagram, Facebook and YouTube Shorts, and the poster to Facebook.")}>
                <FaCheckCircle /> Approve &amp; publish
              </button>
            )}
            {dirty && (
              <button disabled={saving} className={`${btn} bg-kk-gold text-kk-ink hover:bg-kk-amber`}
                onClick={() => update({ status: "changes_requested", scripts: { ...(row.scripts || {}), te, en }, notes: notes || null })}>
                Re-voice my edits
              </button>
            )}
            {!dirty && notes && (
              <button disabled={saving} className={`${btn} bg-kk-gold text-kk-ink hover:bg-kk-amber`}
                onClick={() => update({ status: "changes_requested", notes })}>
                New draft with this note
              </button>
            )}
            {row.status === "failed" && (
              <button disabled={saving} className={`${btn} bg-white/5 text-white/70 hover:text-white`}
                onClick={() => update({ status: row.reel_url ? "ready" : "requested", error_message: null })}>
                {row.reel_url ? "Back to review" : "Try again"}
              </button>
            )}
            {dirty && <button className={`${btn} bg-white/5 text-white/60`} onClick={() => { setDirty(false); load(); }}>Discard edits</button>}
          </div>
        </div>
      )}

      {links.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap text-[11px]">
          {links.map((p, i) => (
            <a key={i} href={p.url} target="_blank" rel="noreferrer"
              className={p.status === "published" ? "text-green-300/80 hover:text-green-300" : "text-red-300/80"}>
              {p.platform}: {p.status}
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
